import {
  boundCheckboxField,
  boundNumberField,
  boundTextField,
  Css,
  FormPageLayout,
  useComputed,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { useCallback, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  createProductOfferingConfigsCompareUrl,
  createProductOfferingConfigsUrl,
  createProductOfferingUrl,
} from "src/RouteUrls";
import { ReadyPlanStatus, SaveProductOfferingConfigInput } from "src/generated/graphqlTypes";
import {
  AddProductOfferingConfig_ReadyPlanOptionsFragment,
  AddProductOfferingConfigMetaQuery,
  AddProductOfferingConfigs_DeltasQuery,
  EditProductOfferingConfigQuery,
  RpoDeltaFragment,
  useAddProductOfferingConfigMetaQuery,
  useAddProductOfferingConfigs_DeltasLazyQuery,
  useEditProductOfferingConfigQuery,
  useSaveProductOfferingConfigMutation,
} from "src/routes/libraries/product-offering-configs/AddProductOfferingConfigPage.generated";
import { ProductOfferingConfigParams } from "src/routes/routesDef";
import { fail, isDefined, queriesResult } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";
import { CreateProjectFromOfferingConfigModal } from "./CreateProjectFromOfferingConfigModal";
import { CostSummarySideBar } from "./components/CostSummarySideBar";
import { OptionsByOptionGroupSection } from "./components/OptionsByOptionGroupSection";

export function AddProductOfferingConfigPage() {
  const { productOfferingId, productOfferingConfigId } = useParams<ProductOfferingConfigParams>();
  // If the config already exists query it and include the RPOs that are already selected
  // We will pass those RPO ids to the metaData query to then fetch the delta costs for those RPOs
  // We could avoid this type of waterfall of queries with a new endpoint, but this seems fine/fast enough
  const configQuery = useEditProductOfferingConfigQuery({
    variables: { id: productOfferingConfigId },
    skip: !productOfferingConfigId,
  });

  const rpoIds =
    configQuery.data?.productOfferingConfig?.readyPlanOptions.filter((rpo) => rpo.active).map((rpo) => rpo.id) ?? [];

  const metaDataQuery = useAddProductOfferingConfigMetaQuery({
    variables: {
      costInput: {
        productOfferingId,
        readyPlanOptionIds: rpoIds,
      },
      programDataInput: { readyPlan: productOfferingId, readyPlanOptions: rpoIds },
      productOfferingId,
    },
    fetchPolicy: "no-cache",
    // To avoid having this query fire twice in the event we are loading an existing config, we will skip the this query until the first loads
    // The queriesResult helper will keep us in a loading state until both queries resolve
    skip: isDefined(productOfferingConfigId) && configQuery.loading,
  });

  return queriesResult([metaDataQuery, configQuery] as const, {
    data: (metaData, configData) => (
      <AddProductOfferingConfigPageView productOfferingConfig={configData?.productOfferingConfig} metaData={metaData} />
    ),
  });
}

type AddOfferingConfigPageProps = {
  metaData: AddProductOfferingConfigMetaQuery;
  productOfferingConfig?: EditProductOfferingConfigQuery["productOfferingConfig"];
};

export type ConfigOptionsAndDelta = {
  rpo: AddProductOfferingConfig_ReadyPlanOptionsFragment;
  delta: RpoDeltaFragment | undefined;
};

export function AddProductOfferingConfigPageView(props: AddOfferingConfigPageProps) {
  const { productOfferingId } = useParams<ProductOfferingConfigParams>();
  const [{ devId, configIds, projectId }] = useQueryParams({
    devId: StringParam,
    configIds: StringParam,
    projectId: StringParam,
  });
  const history = useHistory();
  const { openModal } = useModal();
  const { metaData, productOfferingConfig } = props;
  const isNew = !productOfferingConfig;
  const formState = useFormState({
    config: formConfig,
    init: {
      input: props,
      map: (queryResults) => mapConfigToForm(queryResults),
    },
  });
  const [saveOfferingConfig] = useSaveProductOfferingConfigMutation();
  const [recomputeConfigCost] = useAddProductOfferingConfigs_DeltasLazyQuery({
    fetchPolicy: "cache-and-network",
  });
  // Store/set the latest delta updates for our list of RPOs
  const [deltaRpos, setDeltas] = useState<ConfigOptionsAndDelta[]>(
    metaData.computeProductOfferingCostsAndPrices.productOffering.options.map((rpo) => ({
      rpo,
      delta: metaData.computeProductOfferingCostsAndPrices.allOptions.find((d) => d.readyPlanOption.id === rpo.id),
    })),
  );
  const isArchivedOffering = metaData.productOffering?.status.code === ReadyPlanStatus.Archived;

  // The full list of all available config RPOs to select, should stay static.
  // We only update the RPO delta cost if it changes based on the list of selected RPOS (via `recomputeCosts` util).
  // Group these by the Location if it has a Personalization Type, or by the RPO type if it doesn't.
  const groupedRposByRoomOrType = useMemo(() => {
    const groupedByRoomOrType = deltaRpos
      .sortBy((d) => d.rpo.location?.room?.id.substring(2) ?? d.rpo.type.order.toString())
      .groupBy((res) =>
        res.rpo.optionGroup.personalizationType?.name && res.rpo.location?.room?.name
          ? res.rpo.location.room.name
          : res.rpo.type.name,
      );

    return Object.entries(groupedByRoomOrType)
      .sortBy(([roomOrTypeName, rpos]) => {
        // Check if this is a room (has personalizationType and room name)
        const isRoom = rpos.some(
          (res) => res.rpo.optionGroup.personalizationType?.name && res.rpo.location?.room?.name,
        );
        return [
          isRoom ? 1 : 0, // "not a room" labels first (0), room labels second (1)
          isRoom ? 0 : (rpos[0]?.rpo.type.order ?? 1), // For non-rooms, sort by GOT order; for rooms, use 0 to maintain room name sorting
          roomOrTypeName, // Finally sort by name
        ];
      })
      .map(([roomOrTypeName, rpos]) => {
        const rposGroupedByLocation = rpos
          .sortBy((res) => res.rpo.location?.id.substring(2) ?? "0")
          .groupBy((res) => res.rpo.location?.name ?? "");

        const rposGroupdByLocationAndOptionGroup = Object.entries(rposGroupedByLocation).map(
          ([locationName, rpos2]) => {
            const rposGroupedByOptionGroup = rpos2
              .sortBy((res) => res.rpo.optionGroup.order)
              .groupBy((res) => res.rpo.optionGroup.name);

            return { rposGroupedByOptionGroup, locationName };
          },
        );

        return { rposGroupdByLocationAndOptionGroup, roomOrTypeName };
      });
  }, [deltaRpos]);

  const selectedRpos = useComputed(
    () => formState.value.readyPlanOptions.map((os) => os.readyPlanOptionId).compact(),
    [formState],
  );

  const invalidBaseConfig = useComputed(() => {
    if (!formState.isBaseConfig.value) return;
    const invalidRpos = selectedRpos
      .map((rpoId) => {
        const rpo = metaData.computeProductOfferingCostsAndPrices.productOffering.options.find(
          (rpo) => rpo.id === rpoId,
        );
        if (!rpo?.showInCheckout) return rpo?.name;
      })
      .compact();
    if (invalidRpos.isEmpty) return;
    return `Base config must only include options that are available in Checkout: ${invalidRpos.join(", ")}`;
  }, [formState, selectedRpos]);
  /**
   On each RPO select/deselect:
   - Query the calc for the total config cost (base offering & selected RPOs costs),
    RPO delta costs, and combined program data to calc Cost/SqFt.
   - Then update formState with the refetched values
   **/
  const recomputeCosts = useCallback(
    async (rpo: AddProductOfferingConfig_ReadyPlanOptionsFragment) => {
      // Update form with selected RPO
      formState.readyPlanOptions.set(
        mapSelectedRpoToForm(metaData.computeProductOfferingCostsAndPrices.productOffering.options, formState, rpo),
      );
      const selectedRpos = formState.value.readyPlanOptions.map((os) => os.readyPlanOptionId).compact();
      const res = await recomputeConfigCost({
        variables: {
          costInput: {
            productOfferingId,
            readyPlanOptionIds: selectedRpos,
          },
          programDataInput: {
            readyPlan: productOfferingId,
            readyPlanOptions: selectedRpos,
          },
        },
      });
      // Fetch computed delta/total costs
      setDeltas(
        metaData.computeProductOfferingCostsAndPrices.productOffering.options.map((rpo) => ({
          rpo,
          delta: res.data?.computeProductOfferingCostsAndPrices.allOptions.find((d) => d.readyPlanOption.id === rpo.id),
        })),
      );
      // And update formState costs
      formState.set(mapRecomputedCostToForm(res.data!));
    },
    [formState, recomputeConfigCost, metaData, productOfferingId],
  );

  const missingRequiredRpos = useComputed(() => {
    const requiredRposByGroup = metaData.computeProductOfferingCostsAndPrices.productOffering.options
      .filter((rpo) => rpo.optionGroup.required)
      .groupBy((rpo) => rpo.optionGroup.name);
    const selectedRpos = formState.value.readyPlanOptions.map((rpo) => rpo.readyPlanOptionId).compact();
    const missingRpoGroups = Object.keys(requiredRposByGroup).filter(
      (k) => !requiredRposByGroup[k].some((rpo) => selectedRpos.includes(rpo.id)),
    );
    if (missingRpoGroups.isEmpty) return;
    return `${missingRpoGroups.join(", ")} missing a required option selection`;
  }, [formState]);

  const redirectRoute = useMemo(() => {
    if (configIds) {
      // If we've come from the compare page with a set of configs selected
      // return to the compare page with that set of configIds
      return createProductOfferingConfigsCompareUrl(JSON.parse(configIds), productOfferingId, devId, projectId);
    }
    if (devId || projectId) {
      return createProductOfferingConfigsUrl(devId, projectId, productOfferingId);
    }
    return "/";
  }, [devId, projectId, configIds, productOfferingId]);

  const filteredGroupedRposByOptionGroup = filterSelectedRposByOptionGroup(groupedRposByRoomOrType, selectedRpos);

  // wrap totalCostInCents in a computed to ensure total cost is updated when the RPO is selected/unselected
  const rpoTotalCostInCents = useComputed(() => formState.totalCostInCents.value ?? 0, [formState]);

  return (
    <FormPageLayout
      formSections={[
        {
          title: "Details",
          rows: [
            { name: boundTextField({ placeholder: "Configuration Name Goes Here" }) },
            {
              reactNodeSubtitle:
                "Select options to configure and see cost updates on. Save a configuration to be pulled into underwriting.",
            },
            {
              totalCostInCents: boundNumberField({ type: "cents", readOnly: true }),
              totalCostSqFt: boundNumberField({ type: "cents", label: "Cost/ Sq Ft", readOnly: true }),
            },
            {
              salesPriceInCents: boundNumberField({ type: "cents" }),
            },
            {
              reactNodeHelperText: (
                <p css={Css.sm.gray700.$}>
                  Costs summaries are pulled from a mixture of cost sources, including some that are not awarded bids.
                </p>
              ),
            },
            {
              isBaseConfig: boundCheckboxField({ label: "Set as base" }),
              publishToUnderwriting: boundCheckboxField({
                disabled: metaData.productOffering?.publishToUnderwriting
                  ? false
                  : "Disabled because the product offering is not published to underwriting",
              }),
            },
          ],
        },
        ...groupedRposByRoomOrType.flatMap(({ roomOrTypeName, rposGroupdByLocationAndOptionGroup }) => {
          return {
            title: roomOrTypeName,
            rows: [
              {
                reactNode: rposGroupdByLocationAndOptionGroup.map(({ rposGroupedByOptionGroup, locationName }) => (
                  <OptionsByOptionGroupSection
                    key={locationName}
                    groupName={locationName}
                    groupedRpos={rposGroupedByOptionGroup}
                    formState={formState}
                    recomputeCosts={recomputeCosts}
                  />
                )),
              },
            ],
          };
        }),
      ]}
      formState={formState}
      pageTitle={`${isNew ? "New" : "Edit"} Configurations`}
      breadCrumb={{
        label: metaData.productOffering.name,
        href: createProductOfferingUrl(
          metaData.productOffering.id,
          metaData.productOffering.version.id,
          devId,
          projectId,
        ),
      }}
      submitAction={{
        label: "Save",
        onClick: async () => {
          await saveOfferingConfig({
            variables: { input: mapFormToInput(formState) },
          });
          return history.push(redirectRoute);
        },
        // We think the required RPOs isn't correctly informed so this is a quick workaround
        // disabled: (!!missingRequiredRpos && missingRequiredRpos) || (!!invalidBaseConfig && invalidBaseConfig),
      }}
      cancelAction={{ label: "Cancel", onClick: redirectRoute }}
      tertiaryAction={{
        label: "Create Project",
        onClick: () =>
          openModal({
            content: (
              <CreateProjectFromOfferingConfigModal
                productOfferingConfig={props.productOfferingConfig!}
                developmentId={devId}
                projectId={projectId}
              />
            ),
          }),
        disabled: !props.productOfferingConfig || (!devId && !projectId) || isArchivedOffering,
      }}
      rightSideBar={[
        {
          icon: "dollar",
          render: () => (
            <CostSummarySideBar
              totalCostInCents={rpoTotalCostInCents}
              groupedRposByType={filteredGroupedRposByOptionGroup}
            />
          ),
        },
      ]}
    />
  );
}

export type ProductOfferingConfigForm = Omit<SaveProductOfferingConfigInput, "readyPlanOptionIds"> & {
  readyPlanOptions: { readyPlanOptionId: string | null | undefined }[];
  totalCostInCents: number | null | undefined;
  totalCostSqFt: number | null | undefined;
};
export const formConfig: ObjectConfig<ProductOfferingConfigForm> = {
  id: { type: "value" },
  productOfferingId: { type: "value", rules: [required] },
  name: { type: "value", rules: [required] },
  readyPlanOptions: {
    type: "list",
    rules: [required],
    config: { readyPlanOptionId: { type: "value" } },
  },
  totalCostInCents: { type: "value" },
  totalCostSqFt: { type: "value" },
  salesPriceInCents: { type: "value" },
  isBaseConfig: { type: "value" },
  publishToUnderwriting: { type: "value" },
};

export function mapConfigToForm(queryResults: AddOfferingConfigPageProps): ProductOfferingConfigForm {
  const { productOfferingConfig, metaData } = queryResults;
  const { readyPlanOptions: savedConfigRpos } = productOfferingConfig ?? {};
  const {
    computeProductOfferingCostsAndPrices: { totalCostInCents, productOffering, allOptions },
    computeProgramData: { sellableSqft },
  } = metaData;
  return {
    ...productOfferingConfig,
    productOfferingId: productOffering.id,
    readyPlanOptions: allOptions
      // If RPOs have been saved to the config, preselect its checkbox
      .map((deltaRpo) => ({
        readyPlanOptionId: savedConfigRpos?.find((opt) => opt.id === deltaRpo.readyPlanOption.id)?.id,
      })),
    // TBD: On intial render, if the BE config totalCostInCents is defined
    // default to the BE config totalCostInCents
    totalCostInCents,
    totalCostSqFt: sellableSqft ? Math.round(totalCostInCents / Number(sellableSqft)) : 0,
  };
}

export function mapFormToInput(formState: ObjectState<ProductOfferingConfigForm>): SaveProductOfferingConfigInput {
  // The totalCost fields are frontend-only
  const { totalCostInCents, totalCostSqFt, readyPlanOptions, ...rest } = formState.value;
  return {
    ...rest,
    readyPlanOptionIds: readyPlanOptions?.map((opt) => opt.readyPlanOptionId).compact(),
  };
}

export function mapRecomputedCostToForm(metaData: AddProductOfferingConfigs_DeltasQuery) {
  const { computeProductOfferingCostsAndPrices, computeProgramData } = metaData;
  const totalCostInCents = computeProductOfferingCostsAndPrices.totalCostInCents;
  const sellableSqft = computeProgramData.sellableSqft;
  return {
    totalCostInCents,
    totalCostSqFt: sellableSqft ? Math.round(totalCostInCents / Number(sellableSqft)) : 0,
  };
}

export function mapSelectedRpoToForm(
  allRpos: AddProductOfferingConfig_ReadyPlanOptionsFragment[],
  formState: ObjectState<ProductOfferingConfigForm>,
  newRpo: AddProductOfferingConfig_ReadyPlanOptionsFragment,
): Array<{ readyPlanOptionId: string | undefined | null }> {
  const selectedOptionIds = formState.readyPlanOptions.value.map((os) => os.readyPlanOptionId).compact();
  const wasSelected = selectedOptionIds.includes(newRpo.id);

  const rpog = allRpos.find((rpo) => rpo.id === newRpo.id)?.optionGroup ?? fail();
  // If `SpecLevel:Premium` was already selected, and they click it again, we don't know
  // which "other spec level" to swap to, so just ignore it
  if (rpog.isMultiOptionGroup && wasSelected) {
    return formState.readyPlanOptions.value;
  }

  const newOptionIds = allRpos
    .filter(
      (rpo) =>
        // Keep all current selectedOptionIds (that aren't in our group)
        (selectedOptionIds.includes(rpo.id) && rpo.optionGroup.id !== newRpo.optionGroup.id) ||
        // And `newRpo` if it's being toggled on
        (!wasSelected && rpo.id === newRpo.id),
    )
    .map((rpo) => rpo.id);

  // remove every rpo that has an unfulfilled prereq
  let rposToRemove = getFormRPOsToRemove(allRpos.filter((rpo) => newOptionIds.includes(rpo.id)));
  while (rposToRemove.nonEmpty) {
    newOptionIds.remove(rposToRemove[0].id);
    // ...this is where we should "put back" the first RPO in a multi-group RPOG
    // reload the items to remove now that something has been removed
    rposToRemove = getFormRPOsToRemove(allRpos.filter((rpo) => newOptionIds.includes(rpo.id)));
  }

  // Turn newOptionIds back into the "list of readyPlanOptionId objects" that the formState expects
  return newOptionIds.map((rpoId) => ({ readyPlanOptionId: rpoId }));
}

function getFormRPOsToRemove(selectedRpos: AddProductOfferingConfig_ReadyPlanOptionsFragment[]) {
  const newOptionIds = selectedRpos.map((so) => so.id);
  return selectedRpos.filter((rpo) => {
    if (rpo.optionPrerequisites.isEmpty) return false;
    // We might have prereqs=[sl:essential,sl:premium,addbedroom], which means "either
    // essential or premium is required as a prereq", so we need to group our prereqs by rpog and
    // require at least 1 rpo from each group
    const prereqsByGroup = rpo.optionPrerequisites.groupBy((prereq) => prereq.optionGroup.id);
    return !prereqsByGroup.toValues().every((prereqsInGroup) => {
      // This will be `prereqsInGroup=[sl:essential,sl:premium]` and we just need one of them enabled
      return prereqsInGroup.some((prereq) => newOptionIds.includes(prereq.id));
    });
  });
}

type GroupedRposByRoomOrType = {
  rposGroupdByLocationAndOptionGroup: Array<{
    rposGroupedByOptionGroup: Record<string, ConfigOptionsAndDelta[]>;
    locationName: string;
  }>;
  roomOrTypeName: string;
};

/**
 * Filters grouped RPOs to only include selected options.
 * Used for displaying selected options in the cost summary sidebar.
 */
export function filterSelectedRposByOptionGroup(
  groupedRposByRoomOrType: GroupedRposByRoomOrType[],
  selectedRpoIds: string[],
): Record<string, ConfigOptionsAndDelta[]> {
  const selectedSet = new Set(selectedRpoIds);

  return Object.fromEntries(
    groupedRposByRoomOrType.flatMap(({ rposGroupdByLocationAndOptionGroup }) =>
      rposGroupdByLocationAndOptionGroup.flatMap(({ rposGroupedByOptionGroup }) =>
        rposGroupedByOptionGroup
          .toEntries()
          .map<[string, ConfigOptionsAndDelta[]]>(([groupName, rposList]) => [
            groupName,
            rposList.filter((data) => selectedSet.has(data.rpo.id)),
          ])
          .filter(([, selected]) => selected.nonEmpty),
      ),
    ),
  );
}
